import React, { useState } from 'react'
import {
    Popover,
    PopoverBody,
    PopoverArrow,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverFooter,
    Button,
    Text,
    useDisclosure,
} from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaClock } from 'react-icons/fa6'

import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import TimezoneAwareDateTimeInput from '~components/shared/form/timezone-aware-datetime-input'
import SharedStack from '~components/shared/wrappers/shared-stack'

interface Props {
    isDisabled: boolean
    isLoading: boolean
    onSchedule: (sendTime: number) => void
}

export default function ScheduleSendButton({
    isDisabled,
    isLoading,
    onSchedule,
}: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [sendTime, setSendTime] = useState<number | null | undefined>(null)

    const isInPast = sendTime != null && sendTime < Date.now()
    const canSchedule = sendTime != null && !isInPast

    function handleSchedule() {
        if (!canSchedule) return
        onSchedule(sendTime)
        setSendTime(null)
        onClose()
    }

    function handleClose() {
        setSendTime(null)
        onClose()
    }

    return (
        <Popover
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={handleClose}
            placement="top-end"
            returnFocusOnClose={false}
        >
            <PopoverTrigger>
                <IconButtonWithTooltip
                    h="28px"
                    w="28px"
                    variant="subtle"
                    rounded="full"
                    borderWidth={1}
                    borderColor="gray.200"
                    icon={<Icon as={FaClock} />}
                    label="Schedule send"
                    isDisabled={isDisabled}
                    tooltipOverrides={{
                        isDisabled: isDisabled,
                    }}
                />
            </PopoverTrigger>
            <PopoverContent w="300px" rounded="xl">
                <PopoverArrow />
                <PopoverHeader
                    fontWeight="semibold"
                    fontSize="sm"
                    border="none"
                    pb={0}
                >
                    Schedule Message
                </PopoverHeader>
                <PopoverBody>
                    <SharedStack gap={2}>
                        <Text fontSize="xs" color="gray.500">
                            Choose when to send this message
                        </Text>
                        <TimezoneAwareDateTimeInput
                            value={sendTime}
                            onChange={setSendTime}
                            size="sm"
                        />
                        {isInPast && (
                            <Text fontSize="xs" color="red.500">
                                Please pick a time in the future.
                            </Text>
                        )}
                    </SharedStack>
                </PopoverBody>
                <PopoverFooter border="none" pt={0}>
                    <Button
                        size="sm"
                        colorScheme="brand"
                        rounded="full"
                        w="full"
                        isDisabled={!canSchedule}
                        isLoading={isLoading}
                        onClick={handleSchedule}
                        leftIcon={<Icon as={FaClock} />}
                    >
                        Schedule Send
                    </Button>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    )
}
